<template>
  <div class="edu-page">
    <Navbar />
    <PageBanner :title="pageTitle" />
    <Breadcrumb :items="breadcrumbItems" />

    <div class="edu-layout">
      <!-- Desktop left sidebar (sticky) -->
      <aside class="edu-sidebar">
        <nav :aria-label="t('common.tocLabel')">
          <button
            v-for="section in sections"
            :key="section.id"
            class="edu-sidebar-link"
            :class="{ 'is-active': activeId === section.id }"
            @click="scrollToSection(section.id)"
          >
            {{ section.label }}
          </button>
        </nav>
      </aside>

      <!-- Mobile sticky TOC -->
      <div
        class="mobile-toc"
        role="navigation"
        :aria-label="t('common.tocLabel')"
      >
        <button
          class="toc-header"
          @click="toggleExpand"
          :aria-expanded="isExpanded"
        >
          <span class="toc-header-title">{{ t("common.tocLabel") }}</span>
          <svg
            class="toc-chevron"
            :class="{ 'is-open': isExpanded }"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M5 7.5L10 12.5L15 7.5"
              stroke="#0068A8"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <div class="toc-body">
          <template v-if="isExpanded">
            <button
              v-for="section in sections"
              :key="section.id"
              class="toc-link"
              :class="{ 'is-active': activeId === section.id }"
              @click="collapseAndScroll(section.id)"
            >
              {{ section.label }}
            </button>
          </template>
          <template v-else>
            <button
              class="toc-link toc-current"
              @click="scrollToSection(activeId)"
            >
              {{ sections.find((s) => s.id === activeId)?.label }}
            </button>
          </template>
        </div>
      </div>

      <!-- Main content -->
      <main class="edu-content" @click="handleBodyClick">
        <template v-if="isLoading">
          <section class="edu-section">
            <p class="edu-section-text">{{ t("common.loading") }}</p>
          </section>
        </template>
        <template v-else-if="errorMessage">
          <section class="edu-section">
            <p class="edu-section-text">{{ errorMessage }}</p>
          </section>
        </template>
        <template v-else>
          <!-- Section 1: SNOMED CT 應用 -->
          <section
            v-if="applicationItem"
            id="section-application"
            class="edu-section"
          >
            <h2 class="edu-section-title">{{ applicationTitle }}</h2>
            <div
              v-for="(p, i) in applicationParagraphs"
              :key="i"
              class="edu-section-text"
              style="white-space: pre-line"
            >
              {{ p }}
            </div>
            <div
              v-for="(img, idx) in applicationImages"
              :key="idx"
              class="edu-image-container"
            >
              <img :src="img.src" :alt="img.alt" />
              <span class="image-caption">{{ img.caption }}</span>
            </div>
          </section>

          <hr v-if="applicationItem" class="edu-divider" />

          <!-- Section 2: SNOMED CT 表達式 -->
          <section
            v-if="expressionItem"
            id="section-expression"
            class="edu-section edu-section--bg"
          >
            <h2 class="edu-section-title">{{ expressionTitle }}</h2>
            <div
              v-for="(p, i) in expressionParagraphs"
              :key="i"
              class="edu-section-text"
              style="white-space: pre-line"
            >
              {{ p }}
            </div>
            <div
              v-for="(img, idx) in expressionImages"
              :key="idx"
              class="edu-image-container"
            >
              <img :src="img.src" :alt="img.alt" />
              <span class="image-caption">{{ img.caption }}</span>
            </div>
          </section>

          <hr v-if="expressionItem" class="edu-divider" />

          <!-- Section 3: 以 SNOMED CT 瀏覽器搜尋概念碼 -->
          <section
            v-if="browserConceptItem"
            id="section-browser-concept"
            class="edu-section"
          >
            <h2 class="edu-section-title">{{ browserConceptTitle }}</h2>
            <div
              v-for="(p, i) in browserConceptParagraphs"
              :key="i"
              class="edu-section-text"
              style="white-space: pre-line"
            >
              {{ p }}
            </div>
            <div
              v-for="(img, idx) in browserConceptImages"
              :key="idx"
              class="edu-image-container"
            >
              <img :src="img.src" :alt="img.alt" />
              <span class="image-caption">{{ img.caption }}</span>
            </div>
          </section>

          <hr v-if="browserConceptItem" class="edu-divider" />

          <!-- Section 4: 以 SNOMED CT 瀏覽器搜尋詞彙 : 以心室中膈缺損為例 -->
          <section
            v-if="browserTermItem"
            id="section-browser-term"
            class="edu-section edu-section--bg"
          >
            <h2 class="edu-section-title">{{ browserTermTitle }}</h2>
            <div
              v-for="(p, i) in browserTermParagraphs"
              :key="i"
              class="edu-section-text"
              style="white-space: pre-line"
            >
              {{ p }}
            </div>
            <div
              v-for="(img, idx) in browserTermImages"
              :key="idx"
              class="edu-image-container"
            >
              <img :src="img.src" :alt="img.alt" />
              <span class="image-caption">{{ img.caption }}</span>
            </div>
          </section>

          <hr v-if="browserTermItem" class="edu-divider" />

          <!-- Section 5: SNOMED CT 檔案說明 : 描述及關聯表 -->
          <section
            v-if="filesDescItem"
            id="section-files-desc"
            class="edu-section"
          >
            <h2 class="edu-section-title">{{ filesDescTitle }}</h2>
            <div v-for="(block, idx) in filesDescBlocks" :key="idx">
              <p
                v-if="block.type === 'text'"
                class="edu-section-text"
                style="white-space: pre-line"
              >
                {{ block.paragraph }}
              </p>
              <div
                v-else-if="block.type === 'image'"
                class="edu-image-container"
              >
                <img :src="block.src" :alt="block.alt" />
                <span class="image-caption">{{ block.caption }}</span>
              </div>
            </div>
          </section>

          <hr v-if="filesDescItem" class="edu-divider" />

          <!-- Section 6: SNOMED CT 關聯表之視覺化應用 -->
          <section
            v-if="visualizationItem"
            id="section-visualization"
            class="edu-section edu-section--bg"
          >
            <h2 class="edu-section-title">{{ visualizationTitle }}</h2>
            <div v-for="(block, idx) in visualizationBlocks" :key="idx">
              <p
                v-if="block.type === 'text'"
                class="edu-section-text"
                style="white-space: pre-line"
              >
                {{ block.paragraph }}
              </p>
              <div
                v-else-if="block.type === 'image'"
                class="edu-image-container"
              >
                <img :src="block.src" :alt="block.alt" />
                <span class="image-caption">{{ block.caption }}</span>
              </div>
            </div>
          </section>

          <hr v-if="visualizationItem" class="edu-divider" />

          <!-- Section 7: SNOMED CT 課程 -->
          <section v-if="videosItem" id="section-videos" class="edu-section">
            <h2 class="edu-section-title">{{ videosTitle }}</h2>
            <p class="edu-videos-text">
              想更深入認識 SNOMED CT
              嗎？您可前往「臺灣智慧醫療學校」的「研討會專區」，瀏覽相關課程影片，無須註冊即可直接觀看。
            </p>
            <a
              :href="videosHref"
              target="_blank"
              rel="noopener noreferrer"
              class="edu-videos-btn"
            >
              <span class="edu-videos-btn-text">前往臺灣智慧醫療學校</span>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1.77778 0C0.806166 0 0 0.806166 0 1.77778V14.2222C0 15.1938 0.806166 16 1.77778 16H14.2222C15.1938 16 16 15.1938 16 14.2222V8H14.2222V14.2222H1.77778V1.77778H8V0H1.77778ZM9.77778 0V1.77778H12.9653L4.70486 10.0382L5.96181 11.2951L14.2222 3.03472V6.22222H16V0H9.77778Z"
                  fill="white"
                />
              </svg>
            </a>
          </section>
        </template>
      </main>
      <!-- Lightbox -->
      <ImageLightbox
        :visible="showLightbox"
        :src="currentLightboxImage"
        :alt="pageTitle"
        @close="closeLightbox"
      />
    </div>

    <FooterSection />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import Navbar from "@/components/Navbar.vue";
import PageBanner from "@/components/PageBanner.vue";
import FooterSection from "@/components/FooterSection.vue";
import Breadcrumb from "@/components/Breadcrumb.vue";
import { useEducationToc } from "@/composables/useEducationToc";
import { contentAPI } from "@/services/api";

const { locale, t } = useI18n();

const isLoading = ref(true);
const errorMessage = ref("");
const apiItems = ref([]);

function safeJsonParse(value) {
  if (!value || typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

const localizedItems = computed(() =>
  apiItems.value
    .map((item) => {
      const contentSource =
        locale.value === "en-US" ? item.content_en : item.content;
      return { ...item, _parsed: safeJsonParse(contentSource) };
    })
    .filter((item) => item._parsed && item._parsed.id)
);

const metaItem = computed(() =>
  localizedItems.value.find((item) => item._parsed.id === "meta")
);
const applicationItem = computed(() =>
  localizedItems.value.find((item) => item._parsed.id === "application")
);
const expressionItem = computed(() =>
  localizedItems.value.find((item) => item._parsed.id === "expression")
);
const browserConceptItem = computed(() =>
  localizedItems.value.find((item) => item._parsed.id === "browser-concept")
);
const browserTermItem = computed(() =>
  localizedItems.value.find((item) => item._parsed.id === "browser-term")
);
const filesDescItem = computed(() =>
  localizedItems.value.find((item) => item._parsed.id === "files-desc")
);
const visualizationItem = computed(() =>
  localizedItems.value.find((item) => item._parsed.id === "visualization")
);
const videosItem = computed(() =>
  localizedItems.value.find((item) => item._parsed.id === "videos")
);

const pageTitle = computed(
  () => metaItem.value?._parsed?.bannerTitle || t("snomedCTEducation.title")
);

const applicationTitle = computed(() => {
  const item = applicationItem.value;
  if (!item) return t("snomedCTEducation.application.title");
  return locale.value === "en-US" ? item.title_en || item.title : item.title;
});
const applicationParagraphs = computed(
  () => applicationItem.value?._parsed?.paragraphs || []
);
const applicationImages = computed(
  () => applicationItem.value?._parsed?.images || []
);

const expressionTitle = computed(() => {
  const item = expressionItem.value;
  if (!item) return t("snomedCTEducation.expression.title");
  return locale.value === "en-US" ? item.title_en || item.title : item.title;
});
const expressionParagraphs = computed(
  () => expressionItem.value?._parsed?.paragraphs || []
);
const expressionImages = computed(
  () => expressionItem.value?._parsed?.images || []
);

const browserConceptTitle = computed(() => {
  const item = browserConceptItem.value;
  if (!item) return "以 SNOMED CT 瀏覽器搜尋概念碼";
  return locale.value === "en-US" ? item.title_en || item.title : item.title;
});
const browserConceptParagraphs = computed(
  () => browserConceptItem.value?._parsed?.paragraphs || []
);
const browserConceptImages = computed(
  () => browserConceptItem.value?._parsed?.images || []
);

const browserTermTitle = computed(() => {
  const item = browserTermItem.value;
  if (!item) return "以 SNOMED CT 瀏覽器搜尋詞彙 : 以心室中膈缺損為例";
  return locale.value === "en-US" ? item.title_en || item.title : item.title;
});
const browserTermParagraphs = computed(
  () => browserTermItem.value?._parsed?.paragraphs || []
);
const browserTermImages = computed(
  () => browserTermItem.value?._parsed?.images || []
);

const filesDescTitle = computed(() => {
  const item = filesDescItem.value;
  if (!item) return "SNOMED CT 檔案說明 : 描述及關聯表";
  return locale.value === "en-US" ? item.title_en || item.title : item.title;
});
const filesDescBlocks = computed(
  () => filesDescItem.value?._parsed?.blocks || []
);

const visualizationTitle = computed(() => {
  const item = visualizationItem.value;
  if (!item) return "SNOMED CT 關聯表之視覺化應用";
  return locale.value === "en-US" ? item.title_en || item.title : item.title;
});
const visualizationBlocks = computed(
  () => visualizationItem.value?._parsed?.blocks || []
);

const videosTitle = computed(() => {
  const item = videosItem.value;
  if (!item) return t("snomedCTEducation.videos.title");
  return locale.value === "en-US" ? item.title_en || item.title : item.title;
});
const videosDescription = computed(
  () =>
    videosItem.value?._parsed?.description ||
    t("snomedCTEducation.videos.description")
);
const videosCtaLabel = computed(
  () =>
    videosItem.value?._parsed?.ctaLabel ||
    t("snomedCTEducation.videos.ctaLabel")
);
const videosHref = computed(
  () => videosItem.value?._parsed?.href || "https://smarthealth.mohw.gov.tw"
);
const videosCard = computed(() => videosItem.value?._parsed?.card);
const videosCards = computed(() => {
  const parsed = videosItem.value?._parsed;
  if (!parsed) return [];
  if (Array.isArray(parsed.cards)) return parsed.cards;
  if (parsed.card) return [parsed.card];
  return [];
});

const sections = computed(() => {
  const list = [];
  if (applicationItem.value)
    list.push({ id: "section-application", label: applicationTitle.value });
  if (expressionItem.value)
    list.push({ id: "section-expression", label: expressionTitle.value });
  if (browserConceptItem.value)
    list.push({
      id: "section-browser-concept",
      label: browserConceptTitle.value,
    });
  if (browserTermItem.value)
    list.push({ id: "section-browser-term", label: browserTermTitle.value });
  if (filesDescItem.value)
    list.push({ id: "section-files-desc", label: filesDescTitle.value });
  if (visualizationItem.value)
    list.push({ id: "section-visualization", label: visualizationTitle.value });
  if (videosItem.value)
    list.push({ id: "section-videos", label: videosTitle.value });
  return list;
});

const SECTION_IDS = computed(() => sections.value.map((s) => s.id));

const {
  activeId,
  isExpanded,
  scrollToSection,
  toggleExpand,
  collapseAndScroll,
} = useEducationToc(SECTION_IDS);

const breadcrumbItems = computed(() => [
  { label: t("common.home"), path: "/" },
  { label: t("navbar.snomedCtIntro") },
  { label: t("navbar.snomedCtEducation") },
]);

async function fetchEducationZone() {
  isLoading.value = true;
  errorMessage.value = "";
  try {
    const res = await contentAPI.standardIntro({ section: "education_zone" });
    apiItems.value = Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.message || t("common.systemError");
  } finally {
    isLoading.value = false;
  }
}

import { useLightbox } from "@/composables/useLightbox";
import ImageLightbox from "@/components/ImageLightbox.vue";

const { showLightbox, currentLightboxImage, closeLightbox, handleBodyClick } =
  useLightbox();

onMounted(() => {
  fetchEducationZone();
});
</script>

<style scoped lang="scss">
@use "@/styles/rich-content.scss" as rc;

$nav-h: 58px;
$sidebar-w: 260px;
$toc-bg: #dcf3f9;
$toc-link: #0068a8;
$active-color: #1171dd;
$section-alt-bg: #f9f9f9;
$title-color: #1341ab;
$text-color: #333333;

/* ── Page shell ── */
.edu-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* ── Two-column layout ── */
.edu-layout {
  width: 100%;
  max-width: 1200px;
  margin: 50px auto 80px;
  display: flex;
  align-items: flex-start;
  position: relative;
}

/* ── Desktop sidebar ── */
.edu-sidebar {
  width: $sidebar-w;
  flex: 0 0 $sidebar-w;
  border-right: 1px solid #ededed;
  position: sticky;
  top: calc(#{$nav-h} + 24px);
  align-self: flex-start;
  padding-right: 0;
  max-height: calc(100vh - #{$nav-h} - 48px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #cccccc;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #999999;
  }

  nav {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 0 32px 0 16px;
  }
}

.edu-sidebar-link {
  display: inline-flex;
  width: fit-content;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  padding-bottom: 8px;
  cursor: pointer;
  font-family: "Noto Sans TC", sans-serif;
  font-weight: 500;
  font-size: 18px;
  line-height: 32px;
  color: #696969;
  text-align: left;
  transition: color 0.2s, border-color 0.2s;

  &:hover {
    color: $active-color;
  }

  &.is-active {
    color: $active-color;
    border-bottom-color: $active-color;
  }
}

/* ── Mobile TOC (hidden on desktop) ── */
.mobile-toc {
  display: none;
}

/* ── Content area ── */
:deep(.edu-content) {
  @include rc.rich-content;

  flex: 1;
  min-width: 0;
}

/* ── Sections ── */
.edu-section {
  padding: 48px 64px;
  max-width: 840px;

  &--bg {
    background: transparent;
    max-width: 100%;
    padding-inline: 64px;
    position: relative;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: min(0px, calc(600px - 50vw));
      background: $section-alt-bg;
      z-index: 0;
    }

    > * {
      position: relative;
      z-index: 1;
    }
  }
}

.edu-divider {
  border: none;
  border-top: 1px solid #ededed;
  margin: 0;
}

.edu-section-title {
  margin: 0 0 28px;
  font-family: "Noto Sans TC", sans-serif;
  font-weight: 500;
  font-size: 32px;
  line-height: 44px;
  letter-spacing: 0.02em;
  color: $title-color;
}

.edu-section-text {
  margin: 0 0 20px;
  font-family: "Noto Sans TC", sans-serif;
  font-weight: 400;
  font-size: 20px;
  line-height: 36px;
  color: $text-color;

  &:last-child {
    margin-bottom: 0;
  }
}

.edu-highlight {
  color: $toc-link;
}

/* ── Code block ── */
.edu-code-block {
  margin-top: 24px;
  border: 1px solid #e0ede8;
  border-radius: 4px;
  background: #f0f7f4;
  overflow: hidden;
}

.code-lines {
  padding: 20px 24px;
  font-family: "Courier New", monospace;
  font-size: 14px;
  line-height: 22px;
  color: $text-color;
  overflow-x: auto;
}

.code-line {
  display: flex;
  gap: 12px;
}

.code-num {
  color: #888;
  min-width: 28px;
  user-select: none;
}

.code-caption {
  padding: 8px 24px;
  border-top: 1px solid #e0ede8;
  font-size: 13px;
  color: #666;
  text-align: right;
  margin: 0;
}

/* ── CTA button ── */
.edu-cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-top: 28px;
  padding: 8px 32px;
  border-radius: 100px;
  background: $active-color;
  color: #fff;
  font-family: "Noto Sans TC", sans-serif;
  font-weight: 500;
  font-size: 20px;
  line-height: 32px;
  text-decoration: none;
  transition: background 0.2s;

  &:hover {
    background: #005f96;
  }
}

/* ── Image container ── */
.edu-image-container {
  margin: 24px 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;

  img {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  .image-caption {
    font-size: 14px;
    color: #666;
    margin-top: 4px;
    font-style: italic;
  }
}

/* ── Video Section Custom Styling ── */
.edu-videos-text {
  font-family: "Noto Sans TC", sans-serif;
  font-weight: 400;
  font-size: 20px;
  line-height: 36px;
  letter-spacing: 0%;
  color: $text-color;
  margin-bottom: 28px;
  vertical-align: middle;
}

a.edu-videos-btn,
.edu-videos-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  opacity: 1;
  padding: 8px 32px;
  border-radius: 100px;
  background: $active-color;
  color: #ffffff !important;
  font-family: "Noto Sans TC", sans-serif;
  font-weight: 500;
  font-size: 18px;
  line-height: 32px;
  letter-spacing: 0%;
  text-decoration: none !important;
  vertical-align: middle;
  transition: background 0.2s;

  &,
  &:hover,
  &:visited,
  &:active,
  &:focus {
    color: #ffffff !important;
  }

  .edu-videos-btn-text {
    color: #ffffff !important;
  }

  &:hover {
    background: #005f96;
  }

  svg {
    flex-shrink: 0;
    path {
      fill: #ffffff;
    }
  }
}

/* ── Mobile: ≤960px ── */
@media (max-width: 960px) {
  .edu-layout {
    flex-direction: column;
    margin: 0 0 40px;
    max-width: 100%;
  }

  .edu-sidebar {
    display: none;
  }

  /* Mobile TOC becomes a sticky accordion */
  .mobile-toc {
    display: block;
    width: 100%;
    position: sticky;
    top: $nav-h;
    z-index: 200;
    background: $toc-bg;
    border-bottom: 1px solid #b5dff0;
  }

  .toc-header {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 20px;
    background: none;
    border: none;
    cursor: pointer;
    gap: 8px;
  }

  .toc-header-title {
    font-family: "Noto Sans TC", sans-serif;
    font-weight: 500;
    font-size: 16px;
    color: $toc-link;
  }

  .toc-chevron {
    flex-shrink: 0;
    transition: transform 0.25s ease;

    &.is-open {
      transform: rotate(180deg);
    }
  }

  .toc-body {
    display: flex;
    flex-direction: column;
  }

  .toc-link {
    display: block;
    width: 100%;
    padding: 10px 20px 10px 28px;
    background: none;
    border: none;
    border-left: 3px solid transparent;
    cursor: pointer;
    text-align: left;
    font-family: "Noto Sans TC", sans-serif;
    font-weight: 400;
    font-size: 15px;
    line-height: 24px;
    color: $toc-link;
    transition: border-color 0.2s, background 0.2s;

    &.is-active,
    &.toc-current {
      border-left-color: $toc-link;
      background: rgba(0, 104, 168, 0.06);
    }

    &:hover {
      background: rgba(0, 104, 168, 0.1);
    }
  }

  .edu-section {
    padding: 36px 20px;
    max-width: 100%;

    &--bg {
      padding-inline: 20px;
    }
  }

  .edu-section-title {
    font-size: 24px;
    line-height: 36px;
    margin-bottom: 20px;
  }

  .edu-section-text {
    font-size: 16px;
    line-height: 28px;
  }

  .edu-cta-btn {
    width: 100%;
    max-width: 320px;
    justify-content: center;
    font-size: 18px;
  }

  /* Mobile video section responsive overrides */
  .edu-videos-text {
    font-size: 16px;
    line-height: 28px;
  }

  .edu-videos-btn {
    width: 100%;
    max-width: 320px;
    justify-content: center;
  }
}

@media (max-width: 400px) {
  .toc-header-title {
    font-size: 14px;
  }

  .edu-section-title {
    font-size: 22px;
  }
}

:deep(.edu-content img) {
  cursor: zoom-in;
}
</style>
